import { useMemo } from "react";
import { hourMs } from "verdi-common/timeConstants";
import { MasterIndex } from "verdi-web-client/Models";
import type { IrrigationEventProto } from "verdi-web-client/Models/Scheduling/IrrigationEventProto";

import { useUpcomingZoneIrrigationEvents } from "@/api/hooks/use-upcoming-zone-irrigation-events";
import { CLOCK_CADENCE, useWallClock } from "@/providers/wall-clock";
import { type TimelineItem, type VisualizationItems } from "./event-visualization/event-visualization";

// The visualization should include this much time (in ms) before the current time
export const TIME_BEFORE_TO_INCLUDE_IN_VISUALIZATION_MS = 2 * hourMs;

// The time range that is displayed in the visualization
// The base window is 8 hours
export const BASE_WINDOW_HOURS = 8;
// The maximum extension is 4 hours if any event ends past the initial base window
export const MAX_EXTENSION_HOURS = 4;

/**
 * Future irrigation events grouped by zone id.
 * Supports multiple zones; for single-zone usage pass one key (e.g. the current zone id).
 */
type FutureEventsByZone = Record<string, IrrigationEventProto[]>;

/**
 * Maps a single IrrigationEventProto to TimelineItem.
 */
function mapIrrigationEventToTimelineItem(event: IrrigationEventProto): TimelineItem {
    // IrrigationEventProto types these as Date, but runtime values can arrive as serialized strings
    // (e.g. from JSON deserialization), so we coerce defensively.
    const startTime = event.startTime instanceof Date ? event.startTime : new Date(event.startTime);
    // endTime has the same serialization risk; fall back to startTime + duration if not a Date.
    const endTime =
        event.endTime instanceof Date ? event.endTime : new Date(startTime.getTime() + (event.duration ?? 0));
    // @ts-expect-error event.commandGroup exists, but is not typed
    const isSetEvent = event.commandGroup?.linkedIrrigationSetId !== undefined;

    // @ts-expect-error event.commandGroup exists, but is not typed
    const id = event.commandGroup?.id as string | undefined; // master index references events by command group id

    return {
        startTime,
        endTime,
        isSetEvent,
        id,
    };
}

/**
 * Converts future events by zone into the timeline items shape consumed by EventVisualization.
 */
function zoneEventsToVisualizationItems(futureEventsByZone: FutureEventsByZone): VisualizationItems[] {
    return Object.keys(futureEventsByZone).map((zoneId) => {
        // Map first so dates are coerced, then sort — avoids calling .getTime() on potentially-string dates.
        const events = (futureEventsByZone[zoneId] ?? [])
            .map(mapIrrigationEventToTimelineItem)
            .sort((a, b) => a.startTime.getTime() - b.startTime.getTime());

        return {
            zoneId,
            zoneName: MasterIndex.zone.byID[zoneId]?.name ?? zoneId,
            events,
        };
    });
}

/**
 * Handles data and visualization updates for the on-demand irrigation card.
 */
export const useOnDemandEventData = (zoneId: string) => {
    const nowMs = useWallClock(CLOCK_CADENCE.MINUTES_1);
    // Memoized for referential stability: a plain new Date(nowMs) would be a new object every render, breaking downstream useMemos
    const nowDate = useMemo(() => new Date(nowMs), [nowMs]);

    const { allEvents } = useUpcomingZoneIrrigationEvents({
        zoneId,
        recentlyEndedEventThresholdMs: TIME_BEFORE_TO_INCLUDE_IN_VISUALIZATION_MS,
        maxFutureMs: (BASE_WINDOW_HOURS + MAX_EXTENSION_HOURS) * hourMs - TIME_BEFORE_TO_INCLUDE_IN_VISUALIZATION_MS,
    });

    const visualizationItems = useMemo(
        () => zoneEventsToVisualizationItems({ [zoneId]: allEvents }),
        [zoneId, allEvents],
    );

    const { nearestFutureEvent, nearestEventIsSet } = useMemo(() => {
        if (allEvents.length === 0) return { nearestFutureEvent: undefined, nearestEventIsSet: false };

        // Nearest future event: first in start-time order that hasn't ended
        const newNearestFutureEvent = allEvents.find((e) => e.endTime.getTime() > nowDate.getTime());

        // True when the nearest visible event belongs to a scheduled set and cannot be individually edited.
        const newNearestEventIsSet =
            visualizationItems
                .find((item) => item.zoneId === zoneId)
                // @ts-expect-error commandGroup exists at runtime but is typed as always undefined
                ?.events.find((e) => e.id === newNearestFutureEvent?.commandGroup?.id)?.isSetEvent ?? false;

        return { nearestFutureEvent: newNearestFutureEvent, nearestEventIsSet: newNearestEventIsSet };
    }, [allEvents, nowDate, visualizationItems, zoneId]);

    // @ts-expect-error commandGroup exists at runtime but is typed as always undefined
    const nearestFutureEventId: string | undefined = nearestFutureEvent?.commandGroup?.id; // ID should be the command group ID NOT event.id since this is how master index references events

    return {
        allEvents,
        visualizationItems,
        nearestFutureEvent,
        nearestEventIsSet,
        nearestFutureEventId,
        nowDate,
    };
};
